import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { __ } from '../../helpers/TranslationService';
import { IPersonalData } from '../../interfaces/IRequestForm';
import { PersonalDataActionCreators } from '../../reducers/requestForm/action-creators';
import { IControl, useControl } from '../../hooks/useControl';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { ValidatorsEnum } from '../../hooks/useValidation';
import { normalizePhoneCode } from '../../hooks/usePersonalData';
import useCountryOptions from '../../hooks/useCountryOptions';
import { Modifier } from '../RequestFormPersonalInfo/ControlComponents';

export enum ContactUsFieldsEnumKeys {
    isFemale = 'isFemale',
    firstname = 'firstname',
    lastname = 'lastname',
    countryCode = 'countryCode',
    phone = 'phone',
    phoneCountryCode = 'phoneCountryCode',
    email = 'email',
    message = 'message',
    newsletterSubscription = 'newsletterSubscription',
}

export interface IField {
    name: string;
    control: IControl;
    active: boolean;
}
export interface controlsEnum {
    [key: string]: IField;
}

export const useContactUsFormValidation = ({
    validateNow = false,
}: {
    validateNow?: boolean;
}): controlsEnum => {
    const { requestForm } = useTypedSelector((state) => state);

    const personalRequestForm: IPersonalData = Object.values(ContactUsFieldsEnumKeys).reduce(
        (accumulator, field) => ({
            ...accumulator,
            [field]: requestForm[field] !== undefined ? requestForm[field] : null,
        }),
        {},
    ) as IPersonalData;
    const countryOptions = useCountryOptions(
        personalRequestForm[ContactUsFieldsEnumKeys.countryCode],
    );
    const showNewsletterCheckbox: boolean = !!requestForm.showNewsletterCheckbox;

    const controlsEnum: controlsEnum = {
        [ContactUsFieldsEnumKeys.isFemale]: {
            name: ContactUsFieldsEnumKeys.isFemale,
            control: useControl({
                initialValue: personalRequestForm[ContactUsFieldsEnumKeys.isFemale],
                validators: [ValidatorsEnum.isEmpty],
                options: [
                    { value: '', label: '--', modifier: Modifier.Disabled },
                    { value: 'false', label: __('personalData.male', 'dreamlines') },
                    { value: 'true', label: __('personalData.female', 'dreamlines') },
                ],
                validateNow,
            }),
            active: true,
        },
        [ContactUsFieldsEnumKeys.firstname]: {
            name: ContactUsFieldsEnumKeys.firstname,
            control: useControl({
                initialValue: personalRequestForm[ContactUsFieldsEnumKeys.firstname],
                validators: [ValidatorsEnum.isFirstnameEmpty],
                validateNow,
            }),
            active: true,
        },
        [ContactUsFieldsEnumKeys.lastname]: {
            name: ContactUsFieldsEnumKeys.lastname,
            control: useControl({
                initialValue: personalRequestForm[ContactUsFieldsEnumKeys.lastname],
                validators: [ValidatorsEnum.isLastnameEmpty],
                validateNow,
            }),
            active: true,
        },
        [ContactUsFieldsEnumKeys.countryCode]: {
            name: ContactUsFieldsEnumKeys.countryCode,
            control: useControl({
                initialValue: countryOptions.selectedCode,
                validators: [ValidatorsEnum.isNotDisabled],
                options: countryOptions.options,
                validateNow,
            }),
            active: true,
        },
        [ContactUsFieldsEnumKeys.phoneCountryCode]: {
            name: ContactUsFieldsEnumKeys.phoneCountryCode,
            control: useControl({
                initialValue: normalizePhoneCode(countryOptions.selectedCode),
                validators: [ValidatorsEnum.isPhoneCodeValid],
                validateNow,
            }),
            active: true,
        },
        [ContactUsFieldsEnumKeys.phone]: {
            name: ContactUsFieldsEnumKeys.phone,
            control: useControl({
                initialValue: personalRequestForm[ContactUsFieldsEnumKeys.phone],
                validators: [ValidatorsEnum.isPhoneValid, ValidatorsEnum.isPhoneEmpty],
                validateNow,
            }),
            active: true,
        },
        [ContactUsFieldsEnumKeys.email]: {
            name: ContactUsFieldsEnumKeys.email,
            control: useControl({
                initialValue: personalRequestForm[ContactUsFieldsEnumKeys.email],
                validators: [ValidatorsEnum.isEmailEmpty, ValidatorsEnum.isEmailValid],
                validateNow,
            }),
            active: true,
        },
        [ContactUsFieldsEnumKeys.message]: {
            name: ContactUsFieldsEnumKeys.message,
            control: useControl({
                initialValue: personalRequestForm[ContactUsFieldsEnumKeys.message],
                validateNow,
            }),
            active: true,
        },
        [ContactUsFieldsEnumKeys.newsletterSubscription]: {
            name: ContactUsFieldsEnumKeys.newsletterSubscription,
            control: useControl({
                initialValue: personalRequestForm[ContactUsFieldsEnumKeys.newsletterSubscription],
                validateNow,
            }),
            active: showNewsletterCheckbox,
        },
    };

    const isFormValid: boolean = controlsEnum
        ? Object.values(controlsEnum).every((c) => (c.control.isValid && c.active) || !c.active)
        : false;
    const dispatch = useDispatch();
    const getNewPersonalData = () => {
        const newPersonalData = Object.entries(controlsEnum).reduce(
            (acc, [key, item]) => ({
                ...acc,
                [key]: !item.active ? requestForm[key] : item.control.value,
            }),
            {},
        );
        return newPersonalData;
    };
    const isEqual = (...objects) =>
        objects.every((obj) => JSON.stringify(obj) === JSON.stringify(objects[0]));
    const [newPersonalData, setNewPersonalData] = useState(getNewPersonalData());

    useEffect(() => {
        if (isFormValid) {
            const updatedPersonalData = getNewPersonalData();
            if (!isEqual(updatedPersonalData, newPersonalData)) {
                setNewPersonalData(updatedPersonalData);
            }
        }
    }, [controlsEnum]);

    useEffect(() => {
        if (isFormValid) {
            dispatch(PersonalDataActionCreators.setPersonalData(newPersonalData));
        }
    }, [newPersonalData]);

    return controlsEnum;
};
